import type { DealerSearchRequest, DealerSearchResult } from '@/types/dealer';

/**
 * Browser-side dealer search through same-origin protected API routes.
 *
 * reCAPTCHA is best-effort: a misconfigured secret (or expired token) must
 * never block shoppers from finding dealers. Verification failures are logged
 * and the search still runs.
 */
/**
 * Best-effort reCAPTCHA verification. Never throws — a failed/misconfigured
 * verification is logged and the caller proceeds with the search anyway.
 */
async function verifyDealerSearchRecaptcha(recaptchaToken: string): Promise<void> {
  try {
    const verifyResponse = await fetch('/api/recaptcha/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: recaptchaToken,
        action: 'dealer_search',
      }),
    });

    const verifyData = await verifyResponse.json().catch(() => ({}));
    if (!verifyResponse.ok || !verifyData.success || verifyData.isValidScore === false) {
      console.warn('Dealer search reCAPTCHA verification failed; continuing search', {
        status: verifyResponse.status,
        error: verifyData.error,
        errorCodes: verifyData.errorCodes,
      });
    }
  } catch (recaptchaError) {
    console.error('Dealer search reCAPTCHA verification error:', recaptchaError);
  }
}

/** Normalize the WCL `ReturnValue` (null / {} / array / { Dealers: [] }) into an array. */
function extractDealersData(rawReturn: any): any[] {
  if (Array.isArray(rawReturn)) return rawReturn;
  if (Array.isArray(rawReturn?.Dealers)) return rawReturn.Dealers;
  return [];
}

/** Map a single raw WCL dealer record into a `DealerSearchResult`. */
function mapDealerRecord(dealer: any, index: number): DealerSearchResult {
  return {
    id: dealer?.wcl_account?.wcl_customeridfinance || `dealer-${index}`,
    name: dealer?.wcl_account?.name || 'Unknown Dealer',
    dealerName: dealer?.wcl_account?.name || 'Unknown Dealer',
    address: [
      dealer?.wcl_street,
      dealer?.['_wcl_city_value@OData.Community.Display.V1.FormattedValue'],
      dealer?.['_wcl_state_value@OData.Community.Display.V1.FormattedValue'],
      dealer?.['_wcl_pincode_value@OData.Community.Display.V1.FormattedValue'],
    ]
      .filter(Boolean)
      .join(', '),
    city: dealer?.['_wcl_city_value@OData.Community.Display.V1.FormattedValue'] || '',
    state: dealer?.['_wcl_state_value@OData.Community.Display.V1.FormattedValue'] || '',
    district: dealer?.['_wcl_tehsil_value@OData.Community.Display.V1.FormattedValue'] || '',
    pincode: dealer?.['_wcl_pincode_value@OData.Community.Display.V1.FormattedValue'] || '',
    phone: dealer?.wcl_account?.telephone1 || dealer?.wcl_account?.wcl_owner?.mobilephone || '',
    phone2: dealer?.wcl_account?.wcl_owner?.mobilephone || '',
    email: '',
    latitude: dealer?.wcl_latitude ? parseFloat(dealer.wcl_latitude) : undefined,
    longitude: dealer?.wcl_longitude ? parseFloat(dealer.wcl_longitude) : undefined,
    contactPerson:
      [
        dealer?.wcl_account?.primarycontactid?.firstname,
        dealer?.wcl_account?.primarycontactid?.lastname,
      ]
        .filter(Boolean)
        .join(' ') ||
      [dealer?.wcl_account?.wcl_owner?.firstname, dealer?.wcl_account?.wcl_owner?.lastname]
        .filter(Boolean)
        .join(' ') ||
      '',
    customerIdFinance: dealer?.wcl_account?.wcl_customeridfinance || '',
    distance: '',
    rating: 4.5,
    type: 'Authorized Dealer',
    hours: 'Mon-Sat: 9 AM - 7 PM',
  };
}

export async function searchDealers(params: DealerSearchRequest): Promise<DealerSearchResult[]> {
  try {
    if (params.recaptchaToken) {
      await verifyDealerSearchRecaptcha(params.recaptchaToken);
    }

    const response = await fetch('/api/wcl/dealers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // WCL networkLocator historically expects client type "3" for the
        // public dealer locator (1 = alternate / legacy channel).
        clienttypeId: params.clienttypeId || '1',
        stateId: params.stateId,
        districtId: params.districtId,
        pincode: params.pincode || '',
      }),
      cache: 'no-store',
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(
        errBody.error ||
          errBody.details ||
          `Dealer search proxy error: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    if (data?.IsExceptionOccured) {
      throw new Error(`Dealer search API failed: ${data?.ErrorMessage || 'Unknown error'}`);
    }

    // Normalize ReturnValue — upstream sometimes returns null, {}, or { Dealers: [] }.
    const dealersData = extractDealersData(data?.ReturnValue);

    // Success=false with no rows is a normal "nothing found" response for the locator.
    if (!data?.Success) {
      return [];
    }

    return dealersData.map(mapDealerRecord);
  } catch (error) {
    console.error('Error searching dealers:', error);
    throw error;
  }
}
